import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import { DateType } from 'api/models';
import { SprintPlanningAPI } from 'api/sprint-planning';
import { Sprint, SprintConfig, SprintsList } from 'api/sprint-planning/types';
import { ViewApI } from 'api/views';
import {
  AddTaskMutationType,
  ApiSprintListViewDefinition,
  CompleteSprintPayload,
  SprintGridPayload,
  SprintInfoPayload,
  SprintRemoveTaskPayload,
} from 'components/organisms/sprint-planning/types';
import { fiveMinutes } from './constants';

export const getSprintConfigurationKey = () => 'SPRINT_CONFIG';

const useGetSprintConfiguration = () => {
  return useQuery(
    getSprintConfigurationKey(),
    () => SprintPlanningAPI.getSprintConfiguration(),
    {
      cacheTime: fiveMinutes,
      refetchOnWindowFocus: false,
    },
  );
};

const useUpdateConfigMutation = (): UseMutationResult<
  unknown,
  unknown,
  SprintConfig,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation((payload) => SprintPlanningAPI.updateConfig(payload), {
    onSuccess: async (config) => {
      queryClient.setQueryData(getSprintConfigurationKey(), config);
    },
  });
};

const useCreateSprintMutation = (
  options: UseQueryOptions<Sprint, unknown, Sprint, (string | number)[]>,
): UseMutationResult<Sprint, unknown, SprintInfoPayload, unknown> => {
  return useMutation((payload) => SprintPlanningAPI.createSprint(payload), {
    ...options,
  });
};

const useAddTasksMutation = (
  options,
): UseMutationResult<Sprint, unknown, AddTaskMutationType, unknown> => {
  return useMutation(
    ({ sprintId, payload }) =>
      SprintPlanningAPI.addTasksToSprint(sprintId, payload),
    {
      ...options,
    },
  );
};

export const sprintDetailQueryKey = (sprintId: number): [string, number] => [
  'SPRINT_DETAIL',
  sprintId,
];
const useGetSprintDetailQuery = (
  sprintId: number,
  options?: UseQueryOptions<Sprint, unknown, Sprint, [string, number]>,
) => {
  return useQuery(
    sprintDetailQueryKey(sprintId),
    () => SprintPlanningAPI.getSprint(sprintId),
    {
      enabled: !!sprintId,
      cacheTime: fiveMinutes,
      ...options,
    },
  );
};

const useGetSprintListViewsQuery = (options) => {
  return useQuery(
    'sprint-list-views',
    () => ViewApI.getSprintListViews<ApiSprintListViewDefinition>(),
    {
      ...options,
    },
  );
};

const useGetTaskCountInSprintQuery = ({
  isEnabled,
  sprintId,
  options,
}: {
  isEnabled: boolean;
  sprintId: number;
  options?: UseQueryOptions<number, unknown, number, [string, number]>;
}) => {
  return useQuery(
    ['tasks-count', sprintId],
    () => SprintPlanningAPI.getTaskCountInSprint(sprintId),
    {
      enabled: isEnabled,
      ...options,
    },
  );
};

const useUpdateSprint = (
  options?: UseQueryOptions<Sprint, unknown, Sprint, (string | number)[]>,
): UseMutationResult<Sprint, unknown, SprintGridPayload, unknown> => {
  return useMutation(
    ({ sprintId, payload }: SprintGridPayload) =>
      SprintPlanningAPI.updateSprint(sprintId, payload),
    {
      ...options,
    },
  );
};

const useCompleteSprint = (
  options?: UseQueryOptions<Sprint, unknown, Sprint, string>,
): UseMutationResult<Sprint, unknown, CompleteSprintPayload, unknown> => {
  return useMutation(
    ({ sprintId, payload }: CompleteSprintPayload) =>
      SprintPlanningAPI.completeSprint({ sprintId, payload }),
    {
      ...options,
    },
  );
};

const useRemoveTaskFromSprint = (): UseMutationResult<
  void,
  unknown,
  SprintRemoveTaskPayload,
  unknown
> => {
  return useMutation(({ sprintId, payload }: SprintRemoveTaskPayload) =>
    SprintPlanningAPI.removeTaskFromSprint({ sprintId, payload }),
  );
};
const useDeleteSprint = (): UseMutationResult<
  void,
  unknown,
  { sprintId: number },
  unknown
> => {
  return useMutation(({ sprintId }: { sprintId: number }) =>
    SprintPlanningAPI.deleteSprint({ sprintId }),
  );
};

export const sprintsForTimeRange = 'SPRINTS_FOR_TIME_RANGE';

const useGetSprintsInTimeRange = ({
  startDate,
  endDate,
  projectIds,
  inComplete,
  enabled,
  isAlreadyAddedToPhase = false,
  onSuccess,
  sprintId,
}: {
  startDate: DateType;
  endDate: DateType;
  projectIds?: number;
  inComplete?: boolean;
  enabled: boolean;
  sprintId?: number;
  isAlreadyAddedToPhase?: boolean;
  onSuccess?: (data: SprintsList) => void;
}) => {
  return useQuery(
    [sprintsForTimeRange, startDate, endDate],
    () =>
      SprintPlanningAPI.getSprintsList({
        startDate,
        endDate,
        projectIds,
        inComplete,
        isAlreadyAddedToPhase,
        sprintId,
      }),

    { refetchOnMount: true, enabled, onSuccess },
  );
};

export const tasksInSprintForExternalUser = 'TASKS_IN_SPRINT_FOR_EXTERNAL_USER';

const useGetTasksInSprintForExternalUser = ({ projectId, sprintId }) => {
  return useQuery(
    [tasksInSprintForExternalUser, sprintId],
    () =>
      SprintPlanningAPI.getTasksInSprintForExternalUser({
        projectId,
        sprintId,
      }),
    { refetchOnMount: true },
  );
};

const useSprintsCount = () => {
  return useQuery('sprints-count', () => SprintPlanningAPI.sprintsCount(), {
    refetchOnMount: true,
  });
};

export {
  useGetSprintConfiguration,
  useUpdateConfigMutation,
  useCreateSprintMutation,
  useGetSprintDetailQuery,
  useUpdateSprint,
  useAddTasksMutation,
  useRemoveTaskFromSprint,
  useGetSprintsInTimeRange,
  useGetTasksInSprintForExternalUser,
  useDeleteSprint,
  useCompleteSprint,
  useGetTaskCountInSprintQuery,
  useGetSprintListViewsQuery,
  useSprintsCount,
};
